/**
 * Custom error extractor for Sentry's Angular `ErrorHandler`.
 *
 * The default extractor returns `null` for any thrown value that is not an
 * `Error`, an error-like object, a `string`, or an `HttpErrorResponse`. Sentry
 * then reports every one of those as the same stackless "Handled unknown error",
 * collapsing unrelated failures into a single opaque issue.
 *
 * This extractor defers to the default behaviour first, and only when it yields
 * nothing does it turn the raw value into a real `Error` with a descriptive,
 * serialized message so distinct failures fingerprint into distinct issues.
 */
export function sentryErrorExtractor(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
  const extracted = defaultExtractor(error);
  if (extracted) return extracted;

  return normalizeUnknownError(error);
}

/**
 * Builds a named `Error` describing a value that was thrown but is neither an
 * `Error` nor otherwise extractable, embedding a safe serialization of it so
 * Sentry can group and label the occurrence.
 */
function normalizeUnknownError(error: unknown): Error {
  const normalized = new Error(`Non-error thrown: ${describeUnknownValue(error)}`);
  normalized.name = "UnknownThrownError";
  return normalized;
}

function describeUnknownValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value !== "object") return `${typeof value}(${String(value)})`;

  try {
    const serialized = JSON.stringify(value, buildSafeReplacer());
    if (serialized && serialized !== "{}") return serialized;
  } catch {
    // fall through to the tag below
  }

  return Object.prototype.toString.call(value);
}

/**
 * JSON replacer that drops circular references so serialization never throws.
 */
function buildSafeReplacer(): (key: string, value: unknown) => unknown {
  const seen = new WeakSet<object>();
  return (_key: string, value: unknown) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[Circular]";
      seen.add(value);
    }
    return value;
  };
}
